import React from 'react';
import { Box, Typography, Button, Stack, Alert } from '@mui/material';
import { NotificationItem } from './NotificationItem';
import { UserNotification } from '../../types/user';
import { useNavigate } from 'react-router-dom';

interface NotificationListProps {
  notifications: UserNotification[];
  loading?: boolean;
  onMarkAsRead: (id: string) => void;
  onMarkAsUnread: (id: string) => void;
  onArchive: (id: string) => void;
  onUnarchive: (id: string) => void;
  onDelete: (id: string) => void;
  onMarkAllAsRead?: () => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  emptyMessage?: string;
}

/**
 * NotificationList Component
 * Displays a list of notifications with batch actions
 */
export const NotificationList: React.FC<NotificationListProps> = ({
  notifications,
  loading = false,
  onMarkAsRead,
  onMarkAsUnread,
  onArchive,
  onUnarchive,
  onDelete,
  onMarkAllAsRead,
  onLoadMore,
  hasMore = false,
  emptyMessage = 'No notifications to display',
}) => {
  const navigate = useNavigate();

  const handleNavigate = (url: string) => {
    navigate(url);
  };

  // Count unread notifications
  const unreadCount = notifications.filter((n) => !n.read).length;

  if (loading && notifications.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">Loading notifications...</Typography>
      </Box>
    );
  }

  if (notifications.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">{emptyMessage}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Batch Actions */}
      {unreadCount > 0 && onMarkAllAsRead && (
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
            </Typography>
            <Button size="small" onClick={onMarkAllAsRead}>
              Mark all as read
            </Button>
          </Stack>
        </Box>
      )}

      {/* Notification Items */}
      <Box sx={{ p: 2 }}>
        {notifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onMarkAsRead={onMarkAsRead}
            onMarkAsUnread={onMarkAsUnread}
            onArchive={onArchive}
            onUnarchive={onUnarchive}
            onDelete={onDelete}
            onNavigate={handleNavigate}
          />
        ))}
      </Box>

      {/* Load More */}
      {hasMore && onLoadMore && (
        <Box sx={{ p: 2, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={onLoadMore} disabled={loading}>
            {loading ? 'Loading...' : 'Load More'}
          </Button>
        </Box>
      )}
    </Box>
  );
};
